import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function RefundPolicy() {
  const classes = useStyles();
  const seo = useSEO("refund-policy");

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          Refund Policy
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Effective Date: June 30, 2025
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          At Sugar Cribs, we are committed to maintaining transparency and
          trust between our creators and users. Please read our refund
          policy carefully before making any purchases.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          General Policy
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          All purchases made on{" "}
          <a href="https://SugarCribs.com" className={classes.link}>SugarCribs.com</a> — including
          pay-per-view content, subscriptions, tips, and custom video
          requests — are <strong>final and non-refundable,</strong> except
          where required by law or determined by our internal review team
          under exceptional circumstances.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Eligible Refund Scenarios
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Refunds <strong>may</strong> be granted at the sole discretion of
          Sugar Cribs in the following cases:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Fraudulent Transactions: If a transaction was made using a
            stolen card or unauthorized account access.
          </li>
          <li className={classes.listItem}>
            Duplicate Charges: If a user was charged more than once for the
            same service or content.
          </li>
          <li className={classes.listItem}>
            Content Delivery Failure: If a custom request was accepted and
            paid for but never delivered by the creator.
          </li>
          <li className={classes.listItem}>
            Malfunctioning Content: If purchased content is inaccessible or
            severely corrupted and cannot be resolved after troubleshooting.
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          Ineligible Refund Scenarios
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Refunds <strong>will not</strong> be issued for:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Buyer's remorse or dissatisfaction with content that was
            delivered as described
          </li>
          <li className={classes.listItem}>
            Partially consumed subscriptions (e.g., mid-month cancellations)
          </li>
          <li className={classes.listItem}>Tips or voluntary payments to creators</li>
          <li className={classes.listItem}>Failure to cancel a subscription prior to renewal</li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          Dispute Resolution Process
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          To request a refund, users must submit a support ticket via{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>{" "}
          within <strong>7 days</strong> of the original purchase. Include
          the following:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Username and email</li>
          <li className={classes.listItem}>Transaction ID and date</li>
          <li className={classes.listItem}>Reason for the refund request</li>
          <li className={classes.listItem}>Relevant screenshots (if applicable)</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          Our team will investigate the claim and respond within{" "}
          <strong>5 business days.</strong>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Chargebacks
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Initiating a chargeback without contacting support first may
          result in:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Immediate account suspension or termination</li>
          <li className={classes.listItem}>Forfeiture of future access to Sugar Cribs</li>
          <li className={classes.listItem}>Permanent ban from platform services</li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          Creator Responsibility
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Creators are responsible for fulfilling all custom content orders
          and clearly labeling their content. Repeated refund requests
          related to a creator may result in content review or account
          action.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Contact
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Questions about this policy?
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Reach us anytime at:{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>TOPV1 LLC (d/b/a "Sugar Cribs")</strong>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Email: <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          </Typography>
        </Box>
      </Container>
    </>
  );
}
